<template>
  <div class="flex flex-col flex-1 min-h-0 bg-gray-800">
    <!-- Logo (fixed top) -->
    <div class="flex items-center flex-shrink-0 h-16 px-4 cursor-pointer" @click="clearFilterAndRedirect">
      <img class="w-auto h-8" src="../assets/logo.svg" alt="CARU" />
    </div>

    <!-- Scrollable filters area -->
    <div class="flex-1 min-h-0 overflow-y-auto overscroll-contain">
      <nav class="px-2 py-3 divide-y divide-gray-700 md:px-3 md:py-4">
        <div class="space-y-1">
          <template v-for="quickFilter in quickFilters" v-bind:key="quickFilter.name">
            <template v-if="checkSuperAdminAccess(quickFilter)">
              <template v-if="quickFilter.subItems">
                <NavigationTree :nodes="quickFilter.subItems" :label="quickFilter.name" :hash="quickFilter.hash" :icon="quickFilter.icon" :amount="quickFilter.amount" :color="quickFilter.color" :query="quickFilter.query" :depth="0" :searchQuery="searchQuery">
                </NavigationTree>
              </template>
              <template v-else>
                <div @click="filterClicked(quickFilter.query)" :class="[
                  searchQuery === quickFilter.query
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                  'group flex items-center px-4 py-3 text-base font-medium rounded-md cursor-pointer md:px-2 md:py-1 md:text-sm',
                ]">
                  <component :is="quickFilter.icon" :class="['mr-3 md:mr-2', 'flex-shrink-0', 'h-6 w-6 md:h-4 md:w-4', ...quickFilterColor(quickFilter.hash)]" aria-hidden="true" />

                  <span class="flex-1 min-w-0 mr-2 truncate" :title="quickFilter.name" :data-testid="'quickFilter-' + quickFilter.name">
                    {{ quickFilter.name }}
                  </span>

                  <span v-if="quickFilter.amount || quickFilter.amount == 0" :class="['flex-shrink-0', navigationTextColor(quickFilter.color)]">
                    ({{ quickFilter.amount }})
                  </span>
                </div>
              </template>
            </template>
          </template>
        </div>

        <div class="pt-6 mt-6">
          <div class="space-y-1">
            <div @click="filterClicked(ownerFilter.query)" :class="[
              isFilterActive(ownerFilter.query)
                ? 'bg-gray-900 text-white'
                : 'text-gray-300 hover:bg-gray-700 hover:text-white',
              'group flex items-center px-4 py-3 text-base font-medium rounded-md cursor-pointer md:px-2 md:py-1 md:text-sm',
            ]" v-for="ownerFilter in ownerFilters" v-bind:key="ownerFilter.name">
              <component :is="ownerFilter.icon" class="flex-shrink-0 w-6 h-6 mr-3 text-cyan-200 md:w-4 md:h-4 md:mr-2" aria-hidden="true" />
              {{ ownerFilter.name }}
            </div>
          </div>
        </div>
      </nav>
    </div>

    <!-- Bottom links (collapsible on mobile, always visible on desktop) -->
    <div class="flex-shrink-0 border-t border-gray-400">
      <!-- Mobile toggle button -->
      <button type="button" class="flex items-center justify-center w-full px-4 py-3 mx-auto text-sm font-medium text-gray-300 md:hidden hover:bg-gray-700" @click="bottomLinksOpen = !bottomLinksOpen">
        <ChevronUpIcon :class="['w-5 h-5 text-gray-400 transition-transform duration-200', bottomLinksOpen ? 'rotate-180' : '']" aria-hidden="true" />
      </button>
      <nav class="px-2 divide-y divide-gray-500 md:py-4" :class="[bottomLinksOpen ? 'block pb-3' : 'hidden md:block']">
        <div class="space-y-1">
          <router-link data-testid="usermanagement-navbar-item" :to="{ name: 'userManagement' }" exact-active-class="admin-links-active" class="flex items-center px-4 py-3 text-base font-medium text-gray-300 rounded-md pointer hover:bg-gray-700 hover:text-white group md:px-2 md:py-2 md:text-sm" @click="$emit('navigate')">
            <UserGroupIcon class="flex-shrink-0 w-6 h-6 mr-3 text-cyan-200 md:w-4 md:h-4 md:mr-2" aria-hidden="true"></UserGroupIcon>
            {{ $t("navigationBar.userManagement") }}
          </router-link>
          <router-link v-if="isSuperAdmin" :to="{ name: 'config-templates' }" exact-active-class="admin-links-active" class="flex items-center px-4 py-3 text-base font-medium text-gray-300 rounded-md pointer hover:bg-gray-700 hover:text-white group md:px-2 md:py-2 md:text-sm" @click="$emit('navigate')">
            <CogIcon class="flex-shrink-0 w-6 h-6 mr-3 text-cyan-200 md:w-4 md:h-4 md:mr-2" aria-hidden="true"></CogIcon>
            {{ $t("navigationBar.configTemplates") }}
          </router-link>
          <router-link v-if="isSuperAdmin" :to="{ name: 'device-alarm-email-notification' }" exact-active-class="admin-links-active" class="flex items-center px-4 py-3 text-base font-medium text-gray-300 rounded-md pointer hover:bg-gray-700 hover:text-white group md:px-2 md:py-2 md:text-sm" @click="$emit('navigate')">
            <BellIcon class="flex-shrink-0 w-6 h-6 mr-3 text-cyan-200 md:w-4 md:h-4 md:mr-2" aria-hidden="true"></BellIcon>
            {{ $t("navigationBar.deviceAlarmsNotificationSubscription") }}
          </router-link>
          <a href="https://www.caru-care.com/pages/academy" target="_blank" rel="noopener noreferrer" class="flex items-center px-4 py-3 text-base font-medium text-gray-300 rounded-md pointer hover:bg-gray-700 hover:text-white group md:px-2 md:py-2 md:text-sm">
            <BookOpenIcon class="flex-shrink-0 w-6 h-6 mr-3 text-cyan-200 md:w-4 md:h-4 md:mr-2" aria-hidden="true"></BookOpenIcon>
            {{ $t("navigationBar.docs") }}
          </a>
          <a href="https://www.caru-care.com/pages/revision" target="_blank" rel="noopener noreferrer" class="flex items-center px-4 py-3 text-base font-medium text-gray-300 rounded-md pointer hover:bg-gray-700 hover:text-white group md:px-2 md:py-2 md:text-sm">
            <RefreshIcon class="flex-shrink-0 w-6 h-6 mr-3 text-cyan-200 md:w-4 md:h-4 md:mr-2" aria-hidden="true"></RefreshIcon>
            {{ $t("navigationBar.batteryReplacement") }}
          </a>
        </div>
      </nav>
    </div>
  </div>
</template>
<script>
import { computed, watch, ref } from "vue";
import { useStore } from "vuex";
import { BellIcon, UserGroupIcon, BookOpenIcon, CogIcon, RefreshIcon, ChevronUpIcon } from "@heroicons/vue/solid";
import { hasVersionBelow } from "@/lib/userHasPermission";
import { quickFilterColor } from "@/lib/search";
import NavigationTree from "@/components/NavigationTree.vue";
import { useRouter } from "vue-router";

export default {
  components: {
    BellIcon,
    UserGroupIcon,
    BookOpenIcon,
    NavigationTree,
    RefreshIcon,
    CogIcon,
    ChevronUpIcon
  },

  emits: ['navigate'],

  setup(props, { emit }) {
    const store = useStore();
    const quickFilters = computed(() => store.state.quickFilters);
    const ownerFilters = computed(() => store.state.ownerFilters);
    const isSuperAdmin = computed(() => store.getters["auth/isSuperAdmin"]);
    const deviceStateAggregations = computed(() => store.state.device.deviceStateAggregations);
    const alarmSummaries = computed(() => store.state.device.alarmAggregations);
    const searchQuery = computed(() => store.state.searchQuery);

    const devices = computed(() => Array.from(store.state.device.all.values()));
    const devicesLoading = computed(
      () => store.state.device.loading || store.state.device.loadingDevices
    );
    const tenants = computed(() => store.state.allTenants);

    const router = useRouter();
    const alarmAccessGranted = ref(null);
    const bottomLinksOpen = ref(false);

    setTimeout(() => {
      store.dispatch("loadAllTenants");
      store.dispatch("device/getAggregations");
    }, 0);

    watch(tenants, () => {
      if (tenants.value && tenants.value.length > 0) {
        if (window.requestIdleCallback) {
          window.requestIdleCallback(() => {
            store.dispatch("createOwnerFilters");
          });
        } else {
          setTimeout(() => {
            store.dispatch("createOwnerFilters");
          }, 50);
        }
      }
    });

    const updateFilterAmount = (filters, query, amount) => {
      if (!filters || amount === undefined) return false;
      for (const item of filters) {
        if (item.query === query) {
          item.amount = amount;
          return true;
        }
        if (item.subItems) {
          if (updateFilterAmount(item.subItems, query, amount)) return true;
        }
      }
      return false;
    };

    watch(
      [alarmSummaries, deviceStateAggregations],
      () => {
        if (!quickFilters.value || quickFilters.value.length === 0) return;
        if (!alarmSummaries.value && !deviceStateAggregations.value) return;

        let tmpFilter = JSON.parse(JSON.stringify(quickFilters.value));

        if (alarmSummaries.value) {
          updateFilterAmount(tmpFilter, "alarmsFilter:critical", alarmSummaries.value.critical);
          updateFilterAmount(tmpFilter, "alarmsFilter:major", alarmSummaries.value.major);
          updateFilterAmount(tmpFilter, "alarmsFilter:minor", alarmSummaries.value.minor);
          updateFilterAmount(tmpFilter, "alarmsFilter:warning", alarmSummaries.value.warning);
        }

        if (deviceStateAggregations.value) {
          const online = deviceStateAggregations.value.online ?? 0;
          const offline = deviceStateAggregations.value.offline ?? 0;

          updateFilterAmount(tmpFilter, "", online + offline);
          updateFilterAmount(tmpFilter, "stateFilter:online", deviceStateAggregations.value.online);
          updateFilterAmount(tmpFilter, "stateFilter:offline", deviceStateAggregations.value.offline);
          updateFilterAmount(tmpFilter, "stateFilter:batteryPowered", deviceStateAggregations.value.deviceBatteryPowered);
          updateFilterAmount(tmpFilter, "stateFilter:batteryLow", deviceStateAggregations.value.braceletBatteryLow);
          updateFilterAmount(tmpFilter, "stateFilter:braceletOutOfRange", deviceStateAggregations.value.braceletOutOfRange);
          updateFilterAmount(tmpFilter, "stateFilter:dailyCheckInActive", deviceStateAggregations.value.dailyCheckInEnabled);
          updateFilterAmount(tmpFilter, "stateFilter:dailyCheckInPaused", deviceStateAggregations.value.dailyCheckInPaused);
          updateFilterAmount(tmpFilter, "stateFilter:dailyCheckInMissing", deviceStateAggregations.value.dailyCheckInMissing);
        }

        store.dispatch("setQuickFilterStats", tmpFilter);
      },
      { immediate: true }
    );

    const filterClicked = filter => {
      store.dispatch("addFilterToSearch", filter);
      emit('navigate');
    }

    const navigationTextColor = color => {
      if (color == "green") {
        return "text-green-300";
      } else if (color == "orange") {
        return "text-orange-300";
      } else if (color == "red") {
        return "text-red-300";
      } else if (color == "yellow") {
        return "text-yellow-100";
      } else {
        return "text-gray-300";
      }
    }

    const clearFilterAndRedirect = () => {
      store.dispatch("addFilterToSearch", "");
      router.push({ name: "devices" });
      emit('navigate');
    }

    const checkSuperAdminAccess = quickFilter => {
      if (quickFilter.name.includes("Alarm")) {
        if (isSuperAdmin.value) return true;

        if (alarmAccessGranted.value === true) return true;
        if (devicesLoading.value) return alarmAccessGranted.value !== false;
        if (devices.value.length > 0) {
          alarmAccessGranted.value = devices.value.some(
            device => !hasVersionBelow(device, '2024.0.0')
          );
          return alarmAccessGranted.value;
        }
        return alarmAccessGranted.value === true;
      }
      return true;
    };

    const isFilterActive = filter => searchQuery.value.includes(filter)

    return {
      quickFilters,
      ownerFilters,
      searchQuery,
      isFilterActive,
      isSuperAdmin,
      quickFilterColor,
      filterClicked,
      navigationTextColor,
      checkSuperAdminAccess,
      clearFilterAndRedirect,
      bottomLinksOpen
    };
  },
};
</script>
<style lang="scss" scoped>
.admin-links-active {
  color: white;
  background-color: rgba(17, 24, 39, 1);

  &:hover {
    background-color: rgba(17, 24, 39, 1) !important;
  }
}
</style>
